import React from "react"
import { useLocation } from "react-router-dom"

const CHECK_INTERVAL_MS = 5 * 60 * 1000
const CHUNK_RELOAD_KEY = "cs-chunk-reload"
const CHUNK_RELOAD_GUARD_MS = 30 * 1000

const isChunkLoadError = (reason: any) => {
    const message = `${reason?.name ?? ""} ${reason?.message ?? reason ?? ""}`
    return /ChunkLoadError|Loading (CSS )?chunk [\w-]+ failed/i.test(message)
}

/**
 * Detect when a new version of the web app has been deployed (build/version.json differs from the running bundle)
 * and reload the page on the next route change, so the user gets the new js/css without a manual hard refresh.
 * Also reloads once when a lazy chunk of the old build is no longer available on the server.
 */
const useVersionCheck = () => {
    const location = useLocation()
    const updateAvailable = React.useRef(false)
    const firstRender = React.useRef(true)

    React.useEffect(() => {
        if (process.env.NODE_ENV !== "production") return

        const checkVersion = async () => {
            if (updateAvailable.current) return
            try {
                const res = await fetch(`/version.json?t=${Date.now()}`, { cache: "no-store" })
                if (!res.ok) return
                const { version } = await res.json() as { version?: string }
                if (version && version !== process.env.APP_VERSION) {
                    updateAvailable.current = true
                }
            } catch {
                // network errors are ignored, the check will be retried
            }
        }

        const handleVisibilityChange = () => {
            if (document.visibilityState === "visible") checkVersion()
        }

        const reloadOnChunkError = (reason: any) => {
            if (!isChunkLoadError(reason)) return
            // avoid reload loops if the chunk is really missing on the new build too
            const lastReload = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY) ?? 0)
            if (Date.now() - lastReload < CHUNK_RELOAD_GUARD_MS) return
            sessionStorage.setItem(CHUNK_RELOAD_KEY, `${Date.now()}`)
            window.location.reload()
        }

        // a failed lazy() import is rethrown by React as an uncaught error, a failed import() outside React as a rejection
        const handleError = (event: ErrorEvent) => reloadOnChunkError(event.error)
        const handleUnhandledRejection = (event: PromiseRejectionEvent) => reloadOnChunkError(event.reason)

        checkVersion()
        const id = setInterval(checkVersion, CHECK_INTERVAL_MS)
        document.addEventListener("visibilitychange", handleVisibilityChange)
        window.addEventListener("error", handleError)
        window.addEventListener("unhandledrejection", handleUnhandledRejection)

        return () => {
            clearInterval(id)
            document.removeEventListener("visibilitychange", handleVisibilityChange)
            window.removeEventListener("error", handleError)
            window.removeEventListener("unhandledrejection", handleUnhandledRejection)
        }
    }, [])

    React.useEffect(() => {
        if (firstRender.current) {
            firstRender.current = false
            return
        }
        // the router has already updated the url, so the reload lands on the page the user is navigating to
        if (updateAvailable.current) window.location.reload()
    }, [location.pathname])
}

export default useVersionCheck
